import React, { useState } from 'react'
import moment from 'moment'
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
} from '@material-ui/core'
import { ExpandMore as ExpandMoreIcon, Done as DoneIcon, ErrorOutline as ErrorIcon } from '@material-ui/icons'

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
  priorityChip: {
    fontWeight: 600,
    fontSize: '11px',
  },
  highPriority: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  normalPriority: {
    backgroundColor: '#2196f3',
    color: 'white',
  },
  lowPriority: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  expandButton: {
    transform: 'rotate(0deg)',
    transition: 'transform 0.3s',
  },
  expandButtonOpen: {
    transform: 'rotate(180deg)',
  },
  eventDetails: {
    backgroundColor: '#f5f5f5',
    padding: '12px',
    fontFamily: 'monospace',
    fontSize: '12px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    maxHeight: '300px',
    overflow: 'auto',
  },
  durationCell: {
    fontWeight: 600,
  },
  emptyState: {
    textAlign: 'center',
    padding: '24px',
    color: '#999',
    fontStyle: 'italic',
  },
})

export interface TaskStatus {
  id: string
  priority: number
  handlerName: string
  eventDetails: string
  receivedAt: string
  startedAt?: string
  completedAt?: string
  success?: boolean
  workerId?: string
}

interface TaskTableProps {
  tasks: TaskStatus[]
  type: 'queued' | 'running' | 'completed'
  showWorkerId?: boolean
}

const getPriorityLabel = (priority: number): string => {
  switch (priority) {
    case 1:
      return 'High'
    case 2:
      return 'Normal'
    case 3:
      return 'Low'
    default:
      return 'Unknown'
  }
}

const getPriorityClass = (priority: number, classes: any): string => {
  switch (priority) {
    case 1:
      return classes.highPriority
    case 2:
      return classes.normalPriority
    case 3:
      return classes.lowPriority
    default:
      return classes.normalPriority
  }
}

const TaskRow: React.FC<{ task: TaskStatus; classes: any; showWorkerId?: boolean }> = ({
  task,
  classes,
  showWorkerId,
}) => {
  const [expanded, setExpanded] = useState(false)

  const duration =
    task.startedAt && task.completedAt
      ? moment(task.completedAt).diff(moment(task.startedAt))
      : task.startedAt
      ? moment().diff(moment(task.startedAt))
      : null

  return (
    <>
      <TableRow>
        <TableCell>{task.id}</TableCell>
        {showWorkerId && <TableCell>{task.workerId || '-'}</TableCell>}
        <TableCell>{task.handlerName}</TableCell>
        <TableCell>
          <Chip
            label={getPriorityLabel(task.priority)}
            className={`${classes.priorityChip} ${getPriorityClass(task.priority, classes)}`}
            size="small"
          />
        </TableCell>
        <TableCell>{moment(task.receivedAt).format('M/D/YY h:mm:ss a')}</TableCell>
        <TableCell>{task.startedAt ? moment(task.startedAt).format('M/D/YY h:mm:ss a') : '-'}</TableCell>
        <TableCell>{task.completedAt ? moment(task.completedAt).format('M/D/YY h:mm:ss a') : '-'}</TableCell>
        <TableCell className={classes.durationCell}>{duration !== null ? `${duration}ms` : '-'}</TableCell>
        <TableCell>{task.success !== undefined ? task.success ? <DoneIcon /> : <ErrorIcon /> : '-'}</TableCell>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            className={`${classes.expandButton} ${expanded ? classes.expandButtonOpen : ''}`}
          >
            <ExpandMoreIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={showWorkerId ? 9 : 8} style={{ paddingBottom: 0, paddingTop: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <div className={classes.eventDetails}>{JSON.stringify(JSON.parse(task.eventDetails), null, 2)}</div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export const TaskTable: React.FC<TaskTableProps> = ({ tasks, type, showWorkerId }) => {
  const classes = useStyles()

  if (!tasks || tasks.length === 0) {
    return <div className={classes.emptyState}>No {type} tasks</div>
  }

  return (
    <Table className={classes.table} size="small">
      <TableHead>
        <TableRow>
          <TableCell>Task ID</TableCell>
          {showWorkerId && <TableCell>Worker</TableCell>}
          <TableCell>Handler Name</TableCell>
          <TableCell>Priority</TableCell>
          <TableCell>Received At</TableCell>
          <TableCell>Started At</TableCell>
          <TableCell>Completed At</TableCell>
          <TableCell>Duration</TableCell>
          <TableCell>&nbsp;</TableCell>
          <TableCell>Details</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {tasks.map(task => (
          <TaskRow
            key={`${task.workerId || ''}:${task.id}`}
            task={task}
            classes={classes}
            showWorkerId={showWorkerId}
          />
        ))}
      </TableBody>
    </Table>
  )
}
